import dayjs from 'dayjs';
import * as z from 'zod';

import {
  type Batch,
  ZodBatch,
} from './batch.js';
import {
  type Certification,
  CertificationSchemeCorsia,
  CertificationSchemeEU,
  CertificationType,
  ZodCertification,
  ZodCertificationScheme,
  ZodCertificationSchemeCorsia,
  ZodCertificationSchemeEU,
  ZodCertificationType,
} from './certification.js';
import {
  type CorsiaActual,
  type CorsiaDefault,
  type EuActual,
  type EuDefault,
  type TotalLCAIncludeEmissions,
  ZodCorsiaActual,
  ZodCorsiaDefault,
  ZodEuActual,
  ZodEuDefault,
  ZodTotalLCAIncludeEmissions,
} from './greenhouseGas.js';
import {
  type Product,
  ZodProduct,
} from './product.js';
import {
  type AdjustedLCAChoice,
  type AssuranceLevel,
  ConversionProcess,
  type DocumentationType,
  type SustainabilityTier,
} from './shared.js';
import {
  type Address,
  ZodAddress,
  ZodAdjustedLCAChoice,
  ZodAssuranceLevel,
  ZodDocumentationType,
  ZodSustainabilityTier,
} from './shared.js';

export type Blending = { isBlended: true; blendingDate: string; blendingLocation: Address };

// blending must be true
export const ZodBlending = z.object({
  isBlended: z.literal(true, { error: 'Based on current specifications, the SAF must be blended' }),
  blendingDate: z.iso.date().refine((date) => {
    const d = dayjs(date);
    const now = dayjs();
    return d.isSame(now, 'day') || d.isBefore(now, 'day');
  }, { error: 'Blending date cannot be in future' }),
  blendingLocation: ZodAddress,
});

export type Supplier = {
  siteName: string;
  siteAddress: Address;
};

export const ZodSupplier = z.object({
  siteName: z.string().min(1),
  siteAddress: ZodAddress,
});

export type IssueMetadataBase = {
  /** Set automatically during issuance */
  accountId: string;
  /** Set by registry admin */
  assuranceLevel: AssuranceLevel;
  documentationType: DocumentationType;
  fileTool: string;
  batch: Batch;
  blending: Blending;
  supplier: Supplier;
  product: Product;

  /** Adjusted LCA choice is checked only if carbonIntensityScore is negative. In that case, the Choice is required */
  adjustedLCAChoice?: AdjustedLCAChoice;
  /** Set through `adjustedLCAChoice`, should take priority over computed values */
  adjustedLCAValue?: number;
  /** Set through `adjustedLCAChoice`, should take priority over computed values */
  sustainabilityTier?: SustainabilityTier;
} & { greenhouseGas: TotalLCAIncludeEmissions } & SolidWaste;

/** This could be union, but it makes it very complex to type and use, so optionals+refine are better */
export const ZodSolidWaste = z.object({
  product: z.object({
    feedStock: z.object({
      isSolidWaste: z.boolean(),
    }),
    isFeedStockMsw: z.number().min(1).max(100).optional(),
  }),
  greenhouseGas: z.object({
    recyclingEmissionsCredits: z.number().min(0).optional(),
    landfillEmissionsCredits: z.number().min(0).optional(),
  }),
})
  .refine(data => (data.product.feedStock.isSolidWaste === true ? data.product.isFeedStockMsw !== undefined : true), { error: 'If feedstock is solid waste, then Product -> % of non-biological content must be set', path: ['product', 'isFeedStockMsw'] })
  .refine(data => (data.product.feedStock.isSolidWaste === true ? data.greenhouseGas.recyclingEmissionsCredits !== undefined : true), { error: 'If feedstock is solid waste, then value of Recycling Emissions Credits (RECs) in the total SAF LCA value must be set', path: ['greenhouseGas', 'recyclingEmissionsCredits'] })
  .refine(data => (data.product.feedStock.isSolidWaste === true ? data.greenhouseGas.landfillEmissionsCredits !== undefined : true), { error: 'If feedstock is solid waste, then value of Landfill Emissions Credits (LECs) in the total SAF LCA value must be set', path: ['greenhouseGas', 'landfillEmissionsCredits'] });

/** This is generalized version of potential union we could use to make it easier to work with. These fields are set only if isSolidWaste=true (only one type of feedStock), and used only in corsia calculations */
export type SolidWaste = { product: { feedStock: { isSolidWaste: boolean }; isFeedStockMsw?: number }; greenhouseGas: { recyclingEmissionsCredits?: number; landfillEmissionsCredits?: number } };
const _solidWasteA: SolidWaste = {} as z.infer<typeof ZodSolidWaste>;
const _solidWasteB: z.infer<typeof ZodSolidWaste> = {} as SolidWaste;

export const ZodIssueMetadataBase = z.object({
  assuranceLevel: ZodAssuranceLevel,
  accountId: z.uuid(),
  documentationType: ZodDocumentationType,
  fileTool: z.string(),
  batch: ZodBatch,
  blending: ZodBlending,
  supplier: ZodSupplier,
  product: ZodProduct,
  /** Fields below are checked during issuance process/endpoint, not through zod, because it would interfere with frontend usage of this schema */
  adjustedLCAChoice: ZodAdjustedLCAChoice.optional(),
  adjustedLCAValue: z.number().optional(),
  sustainabilityTier: ZodSustainabilityTier.optional(),
});

/** Branding was added ONLY to differentiate final type from Zod type. When they start to differ, branding can be removed */
export type IssueMetadataCorsiaActual = IssueMetadataBase & { certification: Certification<CertificationSchemeCorsia, CertificationType.Actual>; greenhouseGas: CorsiaActual } & { __brand: 'issuance' };
export type IssueMetadataCorsiaDefault = IssueMetadataBase & { certification: Certification<CertificationSchemeCorsia, CertificationType.Default>; greenhouseGas: CorsiaDefault } & { __brand: 'issuance' };
export type IssueMetadataEuDefault = IssueMetadataBase & { certification: Certification<CertificationSchemeEU, CertificationType.Default>; greenhouseGas: EuDefault } & { __brand: 'issuance' };
export type IssueMetadataEuActual = IssueMetadataBase & { certification: Certification<CertificationSchemeEU, CertificationType.Actual>; greenhouseGas: EuActual } & { __brand: 'issuance' };

export const ZodIssueMetadataCorsiaActual = z.object({
  certification: ZodCertification(ZodCertificationSchemeCorsia, z.literal(CertificationType.Actual)),
  greenhouseGas: ZodCorsiaActual,
});
export const ZodIssueMetadataCorsiaDefault = z.object({
  certification: ZodCertification(ZodCertificationSchemeCorsia, z.literal(CertificationType.Default)),
  greenhouseGas: ZodCorsiaDefault,
});
export const ZodIssueMetadataEuActual = z.object({
  certification: ZodCertification(ZodCertificationSchemeEU, z.literal(CertificationType.Actual)),
  greenhouseGas: ZodEuActual,
});
export const ZodIssueMetadataEuDefault = z.object({
  certification: ZodCertification(ZodCertificationSchemeEU, z.literal(CertificationType.Default)),
  greenhouseGas: ZodEuDefault,
});

/**
 * Custom schema that could be implemented using `z.union` but it would deliver very ugly errors,
 * and `z.discriminatedUnion` cannot be called on nested objects.
 * So it works by first analyzing certification object schema, and then proceeding to precisely selected schema for GHG.
 * Until certification.scheme and .type are set, GHG is not validated leading to much better error reporting
 */
const ZodIssueCertificationAndGHG = z.object({
  // all certification validate only certification and greenhouseGas, therefore these two fields need to be present in initial validation
  certification: z.looseObject({
    scheme: ZodCertificationScheme,
    type: ZodCertificationType,
  }),
  greenhouseGas: z.looseObject({}),
})
  .superRefine((data, ctx) => { // After ensuring proper object schema we can select the schema to use (it works like discriminated union on steroids)
    const schemaToUse = (() => {
      if (CertificationSchemeCorsia.includes(data.certification.scheme)) { // Corsia
        if (data.certification.type === CertificationType.Actual) { // Actual
          return ZodIssueMetadataCorsiaActual;
        } else { // Default
          return ZodIssueMetadataCorsiaDefault;
        }
      } else { // EU
        if (data.certification.type === CertificationType.Actual) { // Actual
          return ZodIssueMetadataEuActual;
        } else { // Default
          return ZodIssueMetadataEuDefault;
        }
      }
    })();
    const result = schemaToUse.safeParse(data);
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
        });
      });
    } else {
      ctx.value = result.data;
    }
  }) as unknown as z.ZodUnion<[typeof ZodIssueMetadataCorsiaActual, typeof ZodIssueMetadataCorsiaDefault, typeof ZodIssueMetadataEuActual, typeof ZodIssueMetadataEuDefault]>;

export const ZodIssueMetadata = ZodIssueMetadataBase
  .and(ZodIssueCertificationAndGHG)
  .and(ZodSolidWaste)
  .and(z.object({ greenhouseGas: ZodTotalLCAIncludeEmissions }))
  .refine((data) => {
    const isCorsia = CertificationSchemeCorsia.includes(data.certification.scheme);
    const isPTL = data.product.conversionProcess === ConversionProcess.PTL;

    return !(isCorsia && isPTL); // corsia does not support PTL
  }, { message: 'Corsia certification scheme does not support PTL conversion processes', path: ['product', 'conversionProcess'] });

/**
 * @NOTE this and all partial types used represent final metadata type
 * ZodIssueMetadata represents type of input metadata, that is adjusted to match final form (defaults inserted etc)
 */
export type IssueMetadata
  = | IssueMetadataCorsiaActual
    | IssueMetadataCorsiaDefault
    | IssueMetadataEuDefault
    | IssueMetadataEuActual;

/**
  * @NOTE ZodIssueMetadata represents type of input metadata, that is adjusted to match final form (defaults inserted etc)
  * IssueMetadata and all partial types used represent final metadata type
  */
export type ZodIssueMetadata = z.infer<typeof ZodIssueMetadata>;

type DotPaths<T> = T extends object
  ? {
      [K in keyof T & string]: T[K] extends object // object or array
        ? T[K] extends Array<any> // array
          ? `${K}` // array
          : `${K}.${DotPaths<T[K]>}` // object (add | `${K}` to support paths to the object itself)
        : `${K}`; // anything else
    }[keyof T & string]
  : never;

/** Branding was added ONLY to differentiate final type from Zod type. When they start to differ, branding can be removed */
export type IssuanceMetadataPaths = Exclude<DotPaths<IssueMetadata>, '__brand'>;

export const issuanceNonUpdateablePaths = ['product.quantityMT', 'accountId'];

export const isCorsiaDefault = (payload: IssueMetadata): payload is IssueMetadataCorsiaDefault => {
  return CertificationSchemeCorsia.includes(payload.certification.scheme) && payload.certification.type === CertificationType.Default;
};

export const isCorsiaActual = (payload: IssueMetadata): payload is IssueMetadataCorsiaActual => {
  return CertificationSchemeCorsia.includes(payload.certification.scheme) && payload.certification.type === CertificationType.Actual;
};

export const isEuActual = (payload: IssueMetadata): payload is IssueMetadataEuActual => {
  return CertificationSchemeEU.includes(payload.certification.scheme) && payload.certification.type === CertificationType.Actual;
};

export const isEuDefault = (payload: IssueMetadata): payload is IssueMetadataEuDefault => {
  return CertificationSchemeEU.includes(payload.certification.scheme) && payload.certification.type === CertificationType.Default;
};
